/**
 * Followups domain API (Spec 086, wire contract from Spec 085).
 * Muster: `lib/api/tags.ts` — thin wrapper around `ApiCore`, no behavior beyond the HTTP call.
 */
import type { ApiCore } from './core';

/** A document reference carried by a Wiedervorlage (085/E12: 0..n documents per aggregate). */
export interface FollowupDocumentReference {
  documentId: string;
  /** `null` when the document has been permanently deleted (086/S12). */
  displayName: string | null;
  inBin: boolean;
}

export interface Followup {
  id: string;
  dueDate: string;
  subject: string;
  /** Wire contract (085): always present, `null` rather than omitted when absent (F12). */
  note: string | null;
  completedAt: string | null;
  source: string;
  documents: FollowupDocumentReference[];
}

export interface CreateFollowupRequest {
  documentId: string;
  dueDate: string;
  subject: string;
  note?: string | null;
}

export interface UpdateFollowupRequest {
  dueDate?: string;
  subject?: string;
  note?: string | null;
}

export type FollowupState = 'open' | 'completed';

// Spec 086/F2: two strategies, chosen per call site, to avoid a double toast (never both):
//   - create/update/remove: global handler shows the ONE toast, via `LOCALIZED_ERROR_CODES`
//     (errorHandler.ts) — the established pattern (`document.notes_too_long`). Their wire test
//     (`followups.spec086.test.ts` "create() propagates a 409 via the shared error handler")
//     requires the global handler to run, so these must NOT suppress it.
//   - list/complete/reopen: suppressed — `Followups.svelte`/`DocumentDetail.svelte` need to react
//     locally (a checkbox revert, F3) that the global handler can't do, and show their own
//     translated toast via `followupErrorMessage` (lib/followups.ts) instead.
const SUPPRESS_GLOBAL_ERROR_HANDLER = true;

export class FollowupsApi {
  constructor(private readonly core: ApiCore) {}

  async list(state: FollowupState = 'open'): Promise<Followup[]> {
    return this.core.fetchJson<Followup[]>(
      `${this.core.baseUrl}/api/v1/followups?state=${state}`,
      { headers: this.core.getAuthHeaders() },
      SUPPRESS_GLOBAL_ERROR_HANDLER,
    );
  }

  async create(payload: CreateFollowupRequest): Promise<Followup> {
    return this.core.fetchJson<Followup>(`${this.core.baseUrl}/api/v1/followups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.core.getAuthHeaders() },
      body: JSON.stringify(payload),
    });
  }

  async update(id: string, payload: UpdateFollowupRequest): Promise<Followup> {
    return this.core.fetchJson<Followup>(`${this.core.baseUrl}/api/v1/followups/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...this.core.getAuthHeaders() },
      body: JSON.stringify(payload),
    });
  }

  async complete(id: string): Promise<Followup> {
    return this.core.fetchJson<Followup>(
      `${this.core.baseUrl}/api/v1/followups/${id}/complete`,
      { method: 'POST', headers: this.core.getAuthHeaders() },
      SUPPRESS_GLOBAL_ERROR_HANDLER,
    );
  }

  async reopen(id: string): Promise<Followup> {
    return this.core.fetchJson<Followup>(
      `${this.core.baseUrl}/api/v1/followups/${id}/reopen`,
      { method: 'POST', headers: this.core.getAuthHeaders() },
      SUPPRESS_GLOBAL_ERROR_HANDLER,
    );
  }

  async remove(id: string): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/followups/${id}`, {
      method: 'DELETE',
      headers: this.core.getAuthHeaders(),
    });
  }
}
